<template>
  <BaseContent
    :pt="{
      root: 'rounded-xl pt-4 pb-1'
    }"
  >
    <DataTable
      v-model:rows="pageSize"
      :first="startIndex"
      :value="mortgageModels"
      paginator
      lazy
      scrollable
      size="small"
      dataKey="id"
      filterDisplay="row"
      :total-records="totalSize"
      :loading="pending"
      :rowsPerPageOptions="[5, 10, 20]"
      currentPageReportTemplate="{currentPage} из {totalPages}"
      :paginatorTemplate="'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport'"
      :pt="{
        thead: 'bg-white',
        column: {
          headerFilterClearButton: 'hidden w-0 m-0 p-0',
          filterMenuButton: 'hidden'
        },
        paginator: {
          current: { class: '!text-[#acacac] text-xs text-light' }
        }
      }"
      @page="onPage($event)"
    >
      <template #empty>
        <div
          v-if="!pending && mortgageModels"
          class="flex flex-col items-center gap-1 text-center"
        >
          <template v-if="hasActiveFilter">
            <span class="text-gray-900 text-base font-medium">Ничего не найдено</span>
            <span class="text-gray-500 text-sm">Измените условия фильтра</span>
          </template>
          <span
            v-else
            class="text-gray-900 text-base font-medium"
            >Заявок пока нет</span
          >
        </div>
      </template>

      <Column
        class="text-sm font-semibold"
        field="id"
        header="Номер заявки"
      >
        <template #body="{ data }">
          <div class="flex flex-col">
            <router-link
              v-slot="{ href, navigate }"
              :to="{
                name: mortgageRouteNames.app,
                params: { id: data.mortgage.id }
              }"
              custom
            >
              <a
                class="w-fit no-underline text-[#151515] text-sm !font-normal"
                :href="href"
                @click="navigate"
                >{{ data.mortgage.id }}</a
              >
            </router-link>
          </div>
        </template>

        <template #filter>
          <div class="relative">
            <InputNumber
              v-model="filter.id"
              :use-grouping="false"
              class="w-full"
              inputClass="pr-6 max-h-8 rounded-xl w-full"
              @blur="onBlur"
              @keydown.enter="onBlur"
            />
            <button
              v-if="isNonEmpty(filter.id)"
              type="button"
              class="absolute w-0 right-1 top-1/2 -translate-y-1/2 leading-none border-none bg-white rounded-full hover:bg-[#bdbdbd] h-5 w-5"
              aria-label="Очистить"
              @click="clearField('id')"
            >
              ×
            </button>
          </div>
        </template>
      </Column>

      <Column
        header="Клиент"
        class="min-w-[280px]"
      >
        <template #body="{ data }">
          <div class="text-[#151515] text-sm font-medium">{{ getMainCustomerFio(data.mortgage) }}</div>

          <span
            v-tooltip.bottom="'Автор заявки'"
            class="text-[13px] font-light text-[#151515]"
          >
            {{ getUserFio(data.user) }}
          </span>
        </template>

        <template #filter>
          <div class="relative">
            <InputText
              v-model="filter.last_name"
              placeholder="ФИО"
              class="pr-6 w-full max-h-8 rounded-xl"
              inputClass="w-full"
              @blur="onBlur"
              @keydown.enter="onBlur"
            />
            <button
              v-if="isNonEmpty(filter.last_name)"
              type="button"
              class="absolute right-1 top-1/2 -translate-y-1/2 leading-none border-none bg-white rounded-full hover:bg-[#bdbdbd] h-5 w-5"
              aria-label="Очистить"
              @click="clearField('last_name')"
            >
              ×
            </button>
          </div>
        </template>
      </Column>

      <Column
        header="Торговая точка"
        class="min-w-[170px]"
      >
        <template #body="{ data }">
          <button
            v-if="data.salespoint"
            v-tooltip.bottom="'Фильтровать по этой точке'"
            type="button"
            class="text-[#151515] text-left text-sm cursor-pointer bg-transparent border-none p-0"
            @click.stop="applySalespointFilter(data.salespoint.id)"
          >
            {{ data.salespoint.title }}
          </button>
          <span
            v-else
            class="text-gray-500"
            >—</span
          >
        </template>

        <template #filter>
          <div class="relative">
            <InputSalespoint
              :key="salespointKey"
              v-model="filter.salespoint_id"
              class="w-full max-h-8 rounded-xl"
              inputClass="pr-7 w-full rounded-xl"
              @blur="onBlur"
              @change="onBlur"
              @keydown.enter="onBlur"
            />
            <button
              v-if="isNonEmpty(filter.salespoint_id)"
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 leading-none border-none bg-white rounded-full hover:bg-[#bdbdbd] h-5 w-5"
              aria-label="Очистить"
              @click="clearSalespoint"
            >
              ×
            </button>
          </div>
        </template>
      </Column>

      <Column header="Статус">
        <template #body="{ data }">
          <MortgageStatus :mortgage="data.mortgage" />
        </template>
        <template #filter>
          <InputMultipleSelect
            v-model="filter.status"
            :options="MORTGAGE_STAGE_LABELS_DICT.filter(stage => stage.code !== 'app_create')"
            :loading="pending"
            option-label="title"
            option-value="code"
            @blur="onBlur"
            @change="onBlur"
          />
        </template>
      </Column>

      <Column
        header="Дата"
        class="min-w-[230px]"
      >
        <template #body="{ data }">
          {{ new Date(data.mortgage.created_at).toLocaleDateString() }}
        </template>

        <template #filter>
          <div class="relative">
            <InputPeriod
              :key="periodKey"
              v-model:date1="filter.date1"
              v-model:date2="filter.date2"
              class="w-full"
              inputClass="pr-8 w-full h-8 rounded-xl "
              :disable-future="true"
              @blur="onBlur"
              @change="onBlur"
              @keydown.enter="onBlur"
            />
            <img
              class="w-3 h-3 absolute right-7 top-[10px]"
              src="@/assets/applications-list-img/Calendar.svg"
            />
            <button
              v-if="isNonEmpty(filter.date1) || isNonEmpty(filter.date2)"
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 leading-none border-none bg-white rounded-full hover:bg-[#bdbdbd] h-5 w-5"
              aria-label="Очистить"
              @click="clearPeriod"
            >
              ×
            </button>
          </div>
        </template>
      </Column>

      <Column
        header="Сумма"
        style="min-width: 150px"
      >
        <template #body="{ data }">
          <span class="whitespace-nowrap">
            {{ Math.round(data.mortgage.credit_amount).toLocaleString() }}₽
          </span>
        </template>

        <template #filter>
          <div class="relative">
            <InputRub
              v-if="filter"
              v-model="filter.credit_amount"
              inputClass="pr-7"
              class="w-full"
              @blur="onBlur"
              @keydown.enter="onBlur"
            />
            <button
              v-if="isNonEmpty(filter?.credit_amount)"
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 leading-none border-none bg-white rounded-full hover:bg-[#bdbdbd] h-5 w-5"
              aria-label="Очистить"
              @click="clearField('credit_amount')"
            >
              ×
            </button>
          </div>
        </template>
      </Column>
      <!-- _bankNameTranslate -->
      <Column
        header="Банк"
        style="min-width: 220px"
      >
        <template #body="{ data }">
          <div class="flex items-center gap-2">
            <BaseBankIcon
              v-if="data.bankName"
              :bankName="data.bankName"
            />
            <span>{{ _bankNameTranslate[data.bankName] || '' }}</span>
          </div>
        </template>
        <template #filter>
          <InputMultipleSelect
            v-model="filter.bankName"
            :options="bankOptions"
            :loading="pending"
            option-label="label"
            option-value="code"
            @blur="onBlur"
            @change="onBlur"
          />
        </template>
      </Column>

      <slot name="columns"></slot>
    </DataTable>
  </BaseContent>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { getUserFio } from '@/entities/user';
import { mortgageRouteNames } from '@/pages/mortgage';
import { BaseBankIcon, BaseContent, InputSalespoint, InputPeriod, InputRub } from '@/shared';
import {
  MORTGAGE_STAGE_LABELS_DICT,
  MortgageStatus,
  MortgageTableFilter,
  getMainCustomerFio,
  isNonEmpty,
  useMortgageInfoModel,
  useMortgageTableModel
} from '@/entities/mortgage';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import DataTable, { DataTablePageEvent } from 'primevue/datatable';
import { InputMultipleSelect } from '@/shared/components/Input/InputMultipleSelect';

const { filter, mortgageModels, getMortgages, pageNumber, pageSize, totalSize, pending, startIndex } =
  useMortgageTableModel('provide');
const { bankOptions, _bankNameTranslate } = useMortgageInfoModel();

const hasActiveFilter = computed(() => Object.values(filter).some(isNonEmpty));

function onPage(event: DataTablePageEvent) {
  if (pageNumber.value !== event.page) {
    pageNumber.value = event.page;
  }
}

let oldFilter: MortgageTableFilter = { ...filter };

function onBlur() {
  nextTick(() => {
    const _filter = JSON.stringify(filter);
    const _oldFilter = JSON.stringify(oldFilter);

    if (_filter !== _oldFilter) {
      oldFilter = { ...filter };
      getMortgages();
    }
  });
}

function clearField<K extends keyof MortgageTableFilter>(key: K) {
  const val = (filter as any)[key];

  let next = null;
  if (typeof val === 'string') next = '';
  else if (Array.isArray(val)) next = [];
  else if (val instanceof Date) next = null;
  else next = null;
  (filter as any)[key] = next;
  onBlur();
}

const salespointKey = ref(0);
const periodKey = ref(0);

function clearSalespoint() {
  filter.salespoint_id = undefined as any;
  salespointKey.value++;
  onBlur();
}

function clearPeriod() {
  filter.date1 = null as any;
  filter.date2 = null as any;
  periodKey.value++;
  onBlur();
}

function applySalespointFilter(id?: number | string) {
  if (id == null) return;

  const normalizedId = typeof id === 'string' ? Number.parseInt(id, 10) : id;
  if (!Number.isFinite(normalizedId)) return;

  filter.salespoint_id = normalizedId as number;

  salespointKey.value++;
  nextTick(onBlur);
}
</script>

<style scoped>
:deep(.p-datatable-thead > tr:last-child > th) {
  padding-top: 0;
  padding-bottom: 1rem;
}

:deep(.p-datatable-thead > tr[data-pc-section='headerrow'] > th) {
  padding-left: 0 !important;
  border: none !important;
  background-color: white;
}

:deep(.p-datatable-tbody > tr > td) {
  border-top: 1px solid #c4c4c4;
  border-bottom: none;
  padding-left: 5px;
}

:deep(.p-datatable-tbody > tr:last-child > td) {
  border-bottom: 1px solid #c4c4c4;
}

:deep(.p-paginator .p-paginator-prev),
:deep(.p-paginator .p-paginator-next) {
  color: var(--field-Label);
}

:deep(.p-paginator .p-paginator-prev .p-paginator-icon),
:deep(.p-paginator .p-paginator-next .p-paginator-icon) {
  width: 22px !important;
  height: 22px !important;
}

.p-datatable-thead > tr > th {
  background-color: white;
}

.p-datatable-thead > tr:first-child > th {
  padding-bottom: 0;
  border: none;
}
</style>
